import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createFastener,
  deleteFastener,
  getFastener,
  listFasteners,
  updateFastener,
  type CreateFastenerBody,
  type Fastener,
  type ListFastenersParams,
  type PaginatedFasteners,
  type UpdateFastenerBody,
} from "../api/fasteners";

export function useFasteners(params: ListFastenersParams) {
  return useQuery<PaginatedFasteners>({
    queryKey: ["fasteners", "list", params],
    queryFn: () => listFasteners(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useFastener(partNo: string | null) {
  return useQuery<Fastener>({
    queryKey: ["fasteners", "detail", partNo],
    queryFn: () => getFastener(partNo as string),
    enabled: !!partNo,
    staleTime: 30_000,
  });
}

export function useCreateFastener() {
  const queryClient = useQueryClient();
  return useMutation<Fastener, Error, CreateFastenerBody>({
    mutationFn: (body) => createFastener(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fasteners"] });
    },
  });
}

export function useUpdateFastener() {
  const queryClient = useQueryClient();
  return useMutation<Fastener, Error, { partNo: string; body: UpdateFastenerBody }>({
    mutationFn: ({ partNo, body }) => updateFastener(partNo, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fasteners"] });
    },
  });
}

/** Soft-delete via DELETE /v1/fasteners/:partNo. */
export function useDeleteFastener() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (partNo) => deleteFastener(partNo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fasteners"] });
    },
  });
}
